import { useCallback, useId, useLayoutEffect, useRef } from "react";

import { cn } from "../lib/cn";

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Visible label. Prefer this over `aria-label`. */
  label?: string;
  /** Quiet helper line under the field. */
  hint?: string;
  /** Error message. Its presence puts the field in the invalid state. */
  error?: string;
  /**
   * Rows the field never shrinks below. Passing `minRows` or `maxRows` turns on
   * auto-grow: the field tracks its content instead of holding a fixed height.
   */
  minRows?: number;
  /**
   * Rows the field never grows past. Content beyond it scrolls inside the
   * field. Only meaningful with auto-grow (see `minRows`).
   */
  maxRows?: number;
  ref?: React.Ref<HTMLTextAreaElement>;
}

/**
 * Multi-line text field. Same paper, label, hint and error wiring as `Input`.
 * Pass `minRows`/`maxRows` to have it grow with what is typed, between those
 * bounds; otherwise it holds `rows` and the user resizes it by hand.
 */
export function Textarea({
  label,
  hint,
  error,
  id,
  className,
  rows,
  minRows,
  maxRows,
  value,
  onInput,
  ref,
  "aria-describedby": ariaDescribedBy,
  ...rest
}: TextareaProps) {
  const generatedId = useId();
  const textareaId = id ?? generatedId;
  const hintId = `${textareaId}-hint`;
  const errorId = `${textareaId}-error`;

  const autoGrow = minRows !== undefined || maxRows !== undefined;
  // With auto-grow the `rows` attribute is the floor the measurement starts
  // from, so browser row metrics - not our own line-height arithmetic - decide
  // the resting height.
  const renderedRows = autoGrow ? (minRows ?? rows ?? 1) : (rows ?? 4);

  const innerRef = useRef<HTMLTextAreaElement | null>(null);

  const fit = useCallback(() => {
    const el = innerRef.current;
    if (!el || !autoGrow) return;

    const styles = window.getComputedStyle(el);
    // `line-height: normal` does not resolve to a length, so fall back to the
    // ratio browsers land near for the body face.
    const lineHeight =
      parseFloat(styles.lineHeight) || parseFloat(styles.fontSize) * 1.2 || 0;
    const paddingY =
      parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
    const borderY =
      parseFloat(styles.borderTopWidth) + parseFloat(styles.borderBottomWidth);
    // `scrollHeight` is the content box plus padding, never the border, while
    // `height` means whichever box `box-sizing` says - so both the measurement
    // and the row bounds need the same correction to be comparable.
    const contentBox = styles.boxSizing === "content-box";
    const trim = contentBox ? -paddingY : borderY;
    const chrome = contentBox ? 0 : paddingY + borderY;

    const minHeight = minRows === undefined ? 0 : minRows * lineHeight + chrome;
    const maxHeight =
      maxRows === undefined ? Infinity : maxRows * lineHeight + chrome;

    // Collapse to the intrinsic height first: without this the box only ever
    // grows, because `scrollHeight` can never fall below a height we already set.
    el.style.height = "auto";
    const naturalHeight = el.scrollHeight + trim;

    el.style.height = `${Math.min(Math.max(naturalHeight, minHeight), maxHeight)}px`;
    el.style.overflowY = naturalHeight > maxHeight ? "auto" : "hidden";
  }, [autoGrow, minRows, maxRows]);

  // Controlled callers re-render on every keystroke; uncontrolled ones do not,
  // so `onInput` below covers typing and this covers value, bounds and mount.
  useLayoutEffect(fit, [fit, value]);

  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el || !autoGrow) return;

    // A narrower field rewraps into more lines. Only width is worth reacting
    // to: reacting to height would be reacting to our own writes.
    let lastWidth = el.clientWidth;
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => {
            if (el.clientWidth === lastWidth) return;
            lastWidth = el.clientWidth;
            fit();
          });
    observer?.observe(el);

    // Fonts swap in after first paint, and the fallback face measures taller or
    // shorter than the real one.
    let live = true;
    void document.fonts?.ready.then(() => {
      if (live) fit();
    });

    return () => {
      live = false;
      observer?.disconnect();
    };
  }, [autoGrow, fit]);

  const describedBy =
    [ariaDescribedBy, hint ? hintId : null, error ? errorId : null]
      .filter(Boolean)
      .join(" ") || undefined;

  const setRef = (node: HTMLTextAreaElement | null) => {
    innerRef.current = node;
    if (typeof ref === "function") {
      ref(node);
    } else if (ref) {
      (ref as React.RefObject<HTMLTextAreaElement | null>).current = node;
    }
  };

  return (
    <div className="flex w-full flex-col gap-1.5">
      {label && (
        <label
          htmlFor={textareaId}
          className="font-body text-sm font-medium text-ink"
        >
          {label}
        </label>
      )}
      <textarea
        ref={setRef}
        id={textareaId}
        rows={renderedRows}
        value={value}
        onInput={(event) => {
          fit();
          onInput?.(event);
        }}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={cn(
          "w-full rounded-paper border bg-card px-3 py-2",
          "font-body text-sm text-ink placeholder:text-faint",
          "transition duration-150 ease-paper",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
          "disabled:cursor-not-allowed disabled:bg-recess disabled:text-faint",
          // Auto-grow owns the height; a hand-dragged one would be thrown away
          // on the next keystroke.
          autoGrow ? "resize-none" : "resize-y",
          error ? "border-ink-stopped" : "border-rule",
          className,
        )}
        {...rest}
      />
      {hint && (
        <p id={hintId} className="font-body text-sm text-annotation">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="font-body text-sm text-ink-stopped">
          {error}
        </p>
      )}
    </div>
  );
}
